/**
 * The wedding's package, resolved ONE way for the day-of timeline generator (server) and the
 * timeline screens (client). 2026-09-25 wedding-planning review gap G7: the generator used a
 * substring match for 'coral'/'ruby' anywhere in notes with a Pearl default, the client read
 * a "Package:" label from notes, the admin list used ILIKE — and nobody read the real FK
 * weddings.package_id -> wedding_packages.
 *
 * Order of trust:
 *   1. weddings.package_id joined to wedding_packages (name "Coral Package" -> "Coral").
 *   2. An explicit "Package: <name>" label in notes, only when it names a real package
 *      (251 live weddings have the label but no package_id, 2026-09-28; wherever both exist
 *      they agree).
 *   3. null. Never a made-up default, never a substring of free text.
 */

export interface PackageRowLike {
  id: number;
  name: string | null;
}

export interface WeddingPackageRef {
  packageId?: number | null;
  notes?: string | null;
}

export interface ResolvedWeddingPackage {
  id: number | null;
  /** Tier name without the " Package" suffix: "Coral", "Pearl", "Ruby", "Garden Banquet". */
  name: string;
  source: 'package_id' | 'notes';
}

export function packageTierName(name: string | null | undefined): string | null {
  if (!name) return null;
  const t = name.replace(/\s+package\s*$/i, '').trim();
  return t || null;
}

/** "Package: Coral, ..." -> "Coral". Only an explicit label; null otherwise. */
export function packageLabelFromNotes(notes: string | null | undefined): string | null {
  if (!notes) return null;
  const m = /Package:\s*([^,\n\[\]]+)/i.exec(notes);
  if (!m) return null;
  // "Pearl - AI Test Wedding" -> "Pearl"
  const label = m[1].split(/\s+-\s+/)[0].trim();
  return label || null;
}

export function resolveWeddingPackage(
  wedding: WeddingPackageRef | null | undefined,
  packages: PackageRowLike[] | null | undefined,
): ResolvedWeddingPackage | null {
  if (!wedding) return null;
  const list = Array.isArray(packages) ? packages : [];

  if (wedding.packageId != null) {
    const row = list.find((p) => p.id === wedding.packageId);
    const tier = packageTierName(row?.name);
    if (row && tier) return { id: row.id, name: tier, source: 'package_id' };
  }

  const label = packageLabelFromNotes(wedding.notes);
  if (!label) return null;
  if (list.length === 0) return { id: null, name: label, source: 'notes' };
  const match = list.find((p) => (packageTierName(p.name) || '').toLowerCase() === label.toLowerCase());
  const tier = packageTierName(match?.name);
  return match && tier ? { id: match.id, name: tier, source: 'notes' } : null;
}
